/**
 * Word of the Day Components
 * Manages word of the day feature for students and teachers
 */

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

/**
 * Custom hook for managing Word of the Day state and handlers
 */
export const useWordOfDayManagement = ({ currentUser }) => {
  const [words, setWords] = useState([]);
  const [newWord, setNewWord] = useState({
    word: '',
    definition: '',
    example: '',
    partOfSpeech: ''
  });
  const [editingWordId, setEditingWordId] = useState(null);
  const [editWordForm, setEditWordForm] = useState({
    word: '',
    definition: '',
    example: '',
    partOfSpeech: ''
  });

  const [quotes, setQuotes] = useState([]);
  const [newQuote, setNewQuote] = useState({
    quote: '',
    author: ''
  });
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [editQuoteForm, setEditQuoteForm] = useState({
    quote: '',
    author: ''
  });

  // Listen for Words
  useEffect(() => {
    if (!currentUser) {
      setWords([]);
      return;
    }

    const qWords = query(collection(db, 'wordOfDay'), orderBy('createdAt', 'desc'));
    const unsubscribeWords = onSnapshot(qWords, (snapshot) => {
      const wordsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWords(wordsData);
    }, (error) => console.error("Error fetching words:", error));

    return () => unsubscribeWords();
  }, [currentUser]);

  // Listen for Quotes
  useEffect(() => {
    if (!currentUser) {
      setQuotes([]);
      return;
    }

    const qQuotes = query(collection(db, 'quoteOfDay'), orderBy('createdAt', 'desc'));
    const unsubscribeQuotes = onSnapshot(qQuotes, (snapshot) => {
      const quotesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setQuotes(quotesData);
    }, (error) => console.error("Error fetching quotes:", error));

    return () => unsubscribeQuotes();
  }, [currentUser]);

  const handleAddWord = async () => {
    if (!newWord.word.trim() || !newWord.definition.trim()) {
      alert('Please provide a word and definition.');
      return;
    }

    try {
      await addDoc(collection(db, 'wordOfDay'), {
        word: newWord.word.trim(),
        definition: newWord.definition.trim(),
        example: newWord.example.trim() || '',
        partOfSpeech: newWord.partOfSpeech.trim() || '',
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      });

      setNewWord({ word: '', definition: '', example: '', partOfSpeech: '' });
    } catch (error) {
      console.error("Error adding word:", error.message);
      alert('Failed to add word. Please try again.');
    }
  };

  const handleStartEditWord = (word) => {
    setEditingWordId(word.id);
    setEditWordForm({
      word: word.word || '',
      definition: word.definition || '',
      example: word.example || '',
      partOfSpeech: word.partOfSpeech || ''
    });
  };

  const handleSaveWordEdit = async () => {
    if (!editingWordId || !editWordForm.word.trim() || !editWordForm.definition.trim()) {
      alert('Please provide a word and definition.');
      return;
    }

    try {
      const ref = doc(db, 'wordOfDay', editingWordId);
      await updateDoc(ref, {
        word: editWordForm.word.trim(),
        definition: editWordForm.definition.trim(),
        example: editWordForm.example.trim() || '',
        partOfSpeech: editWordForm.partOfSpeech.trim() || ''
      });
      setEditingWordId(null);
      setEditWordForm({ word: '', definition: '', example: '', partOfSpeech: '' });
    } catch (error) {
      console.error('Error saving word edit:', error);
      alert('Failed to save changes.');
    }
  };

  const handleCancelEditWord = () => {
    setEditingWordId(null);
    setEditWordForm({ word: '', definition: '', example: '', partOfSpeech: '' });
  };

  const handleDeleteWord = async (wordId) => {
    try {
      const confirmDelete = window.confirm('Delete this word? This cannot be undone.');
      if (!confirmDelete) return;
      await deleteDoc(doc(db, 'wordOfDay', wordId));
    } catch (error) {
      console.error('Error deleting word:', error);
      alert('Failed to delete word.');
    }
  };

  // Get today's word based on date
  const getTodaysWord = () => {
    if (words.length === 0) return null;
    
    // Calculate days since epoch to get a consistent index
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const daysSinceStartOfYear = Math.floor((today - startOfYear) / (1000 * 60 * 60 * 24));
    
    // Sort words by creation date for consistent ordering
    const sortedWords = [...words].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateA - dateB;
    });
    
    // Use modulo to cycle through words
    const index = daysSinceStartOfYear % sortedWords.length;
    return sortedWords[index];
  };

  const handleAddQuote = async () => {
    if (!newQuote.quote.trim()) {
      alert('Please provide a quote.');
      return;
    }

    try {
      await addDoc(collection(db, 'quoteOfDay'), {
        quote: newQuote.quote.trim(),
        author: newQuote.author.trim() || '',
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      });

      setNewQuote({ quote: '', author: '' });
    } catch (error) {
      console.error("Error adding quote:", error.message);
      alert('Failed to add quote. Please try again.');
    }
  };

  const handleStartEditQuote = (quote) => {
    setEditingQuoteId(quote.id);
    setEditQuoteForm({
      quote: quote.quote || '',
      author: quote.author || ''
    });
  };

  const handleSaveQuoteEdit = async () => {
    if (!editingQuoteId || !editQuoteForm.quote.trim()) {
      alert('Please provide a quote.');
      return;
    }

    try {
      const ref = doc(db, 'quoteOfDay', editingQuoteId);
      await updateDoc(ref, {
        quote: editQuoteForm.quote.trim(),
        author: editQuoteForm.author.trim() || ''
      });
      setEditingQuoteId(null);
      setEditQuoteForm({ quote: '', author: '' });
    } catch (error) {
      console.error('Error saving quote edit:', error);
      alert('Failed to save changes.');
    }
  };

  const handleCancelEditQuote = () => {
    setEditingQuoteId(null);
    setEditQuoteForm({ quote: '', author: '' });
  };

  const handleDeleteQuote = async (quoteId) => {
    try {
      const confirmDelete = window.confirm('Delete this quote? This cannot be undone.');
      if (!confirmDelete) return;
      await deleteDoc(doc(db, 'quoteOfDay', quoteId));
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Failed to delete quote.');
    }
  };

  return {
    words,
    newWord,
    setNewWord,
    handleAddWord,
    editingWordId,
    editWordForm,
    setEditWordForm,
    handleStartEditWord,
    handleSaveWordEdit,
    handleCancelEditWord,
    handleDeleteWord,
    getTodaysWord,
    quotes,
    newQuote,
    setNewQuote,
    handleAddQuote,
    editingQuoteId,
    editQuoteForm,
    setEditQuoteForm,
    handleStartEditQuote,
    handleSaveQuoteEdit,
    handleCancelEditQuote,
    handleDeleteQuote
  };
};

/**
 * Word of the Day Display Component (for students on Resources page)
 */
export const WordOfDayDisplay = ({ words }) => {
  const getTodaysWord = () => {
    if (words.length === 0) return null;
    
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const daysSinceStartOfYear = Math.floor((today - startOfYear) / (1000 * 60 * 60 * 24));
    
    const sortedWords = [...words].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateA - dateB;
    });
    
    const index = daysSinceStartOfYear % sortedWords.length;
    return sortedWords[index];
  };

  const todaysWord = getTodaysWord();

  if (!todaysWord) {
    return null;
  }

  return (
    <div className="word-of-day-container">
      <div className="word-of-day-title">Word of the Day</div>
      <div className="word-of-day-word">
        {todaysWord.word}
        {todaysWord.partOfSpeech && (
          <span className="word-of-day-part-of-speech"> ({todaysWord.partOfSpeech})</span>
        )}
      </div>
      <div className="word-of-day-definition">{todaysWord.definition}</div>
      {todaysWord.example && (
        <div className="word-of-day-example">
          <em>Example: {todaysWord.example}</em>
        </div>
      )}
    </div>
  );
};

/**
 * Quote of the Day Display Component (for students on Resources page)
 */
export const QuoteOfDayDisplay = ({ quotes }) => {
  const getTodaysQuote = () => {
    if (quotes.length === 0) return null;
    
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const daysSinceStartOfYear = Math.floor((today - startOfYear) / (1000 * 60 * 60 * 24));
    
    const sortedQuotes = [...quotes].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateA - dateB;
    });
    
    const index = daysSinceStartOfYear % sortedQuotes.length;
    return sortedQuotes[index];
  };

  const todaysQuote = getTodaysQuote();

  if (!todaysQuote) {
    return null;
  }

  return (
    <div className="word-of-day-container">
      <div className="word-of-day-title">Quote of the Day</div>
      <div className="quote-of-day-text">"{todaysQuote.quote}"</div>
      {todaysQuote.author && (
        <div className="quote-of-day-author">— {todaysQuote.author}</div>
      )}
    </div>
  );
};

/**
 * Word of the Day Management View Component (for teachers)
 */
export const WordOfDayManagementView = ({
  words,
  newWord,
  setNewWord,
  handleAddWord,
  editingWordId,
  editWordForm,
  setEditWordForm,
  handleStartEditWord,
  handleSaveWordEdit,
  handleCancelEditWord,
  handleDeleteWord,
  quotes,
  newQuote,
  setNewQuote,
  handleAddQuote,
  editingQuoteId,
  editQuoteForm,
  setEditQuoteForm,
  handleStartEditQuote,
  handleSaveQuoteEdit,
  handleCancelEditQuote,
  handleDeleteQuote
}) => {
  const isEditingWord = editingWordId !== null;
  const currentWordForm = isEditingWord ? editWordForm : newWord;
  const setCurrentWordForm = isEditingWord ? setEditWordForm : setNewWord;

  const isEditingQuote = editingQuoteId !== null;
  const currentQuoteForm = isEditingQuote ? editQuoteForm : newQuote;
  const setCurrentQuoteForm = isEditingQuote ? setEditQuoteForm : setNewQuote;

  return (
    <div className="post post-margin-bottom">
      <h2>Word & Quote of the Day Management</h2>
      <p className="transcript-description">
        Add and manage words and quotes for the daily features. They will automatically rotate daily based on the date.
      </p>

      {/* Word Section */}
      <div style={{ marginBottom: 'var(--spacing-3xl)' }}>
        <h3 style={{ marginBottom: 'var(--spacing-lg)' }}>Words</h3>
        
        {/* Create/Edit Word Form */}
        <div className="create-post">
          <h4>{isEditingWord ? 'Edit Word' : 'Add New Word'}</h4>
          <input
            type="text"
            placeholder="Word"
            className="post-title-input"
            value={currentWordForm.word}
            onChange={(e) => setCurrentWordForm({ ...currentWordForm, word: e.target.value })}
          />
          <input
            type="text"
            placeholder="Part of Speech (e.g., noun, verb, adjective)"
            className="post-title-input"
            value={currentWordForm.partOfSpeech}
            onChange={(e) => setCurrentWordForm({ ...currentWordForm, partOfSpeech: e.target.value })}
          />
          <textarea
            placeholder="Definition..."
            className="post-content-input"
            value={currentWordForm.definition}
            onChange={(e) => setCurrentWordForm({ ...currentWordForm, definition: e.target.value })}
            rows={4}
          />
          <textarea
            placeholder="Example sentence (optional)..."
            className="post-content-input"
            value={currentWordForm.example}
            onChange={(e) => setCurrentWordForm({ ...currentWordForm, example: e.target.value })}
            rows={2}
          />
          <div className="form-actions">
            {isEditingWord ? (
              <>
                <button className="proposal-approve-btn" onClick={handleSaveWordEdit}>
                  Save Changes
                </button>
                <button className="proposal-cancel-btn" onClick={handleCancelEditWord}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="proposal-approve-btn" onClick={handleAddWord}>
                Add Word
              </button>
            )}
          </div>
        </div>

        {/* Existing Words List */}
        {words.length > 0 && (
          <div className="resources-management-list" style={{ marginTop: 'var(--spacing-2xl)' }}>
            <h4>Existing Words ({words.length})</h4>
            {words.map((word) => (
              <div key={word.id} className="resource-management-item">
                <div className="resource-info">
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
                    <h4 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{word.word}</h4>
                    {word.partOfSpeech && (
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                        ({word.partOfSpeech})
                      </span>
                    )}
                  </div>
                  <p style={{ margin: 'var(--spacing-xs) 0', color: 'var(--color-text-primary)' }}>
                    {word.definition}
                  </p>
                  {word.example && (
                    <p style={{ margin: 'var(--spacing-xs) 0', fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
                      Example: {word.example}
                    </p>
                  )}
                  <span className="individual-dates" style={{ display: 'block', marginTop: 'var(--spacing-sm)' }}>
                    Added: {new Date(word.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="proposal-actions">
                  <button className="proposal-edit-btn" onClick={() => handleStartEditWord(word)}>
                    Edit
                  </button>
                  <button className="proposal-reject-btn" onClick={() => handleDeleteWord(word.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {words.length === 0 && (
          <p className="no-posts" style={{ marginTop: 'var(--spacing-xl)' }}>
            No words added yet. Add your first word above!
          </p>
        )}
      </div>

      {/* Quote Section */}
      <div>
        <h3 style={{ marginBottom: 'var(--spacing-lg)' }}>Quotes</h3>
        
        {/* Create/Edit Quote Form */}
        <div className="create-post">
          <h4>{isEditingQuote ? 'Edit Quote' : 'Add New Quote'}</h4>
          <textarea
            placeholder="Quote..."
            className="post-content-input"
            value={currentQuoteForm.quote}
            onChange={(e) => setCurrentQuoteForm({ ...currentQuoteForm, quote: e.target.value })}
            rows={4}
          />
          <input
            type="text"
            placeholder="Author (optional)"
            className="post-title-input"
            value={currentQuoteForm.author}
            onChange={(e) => setCurrentQuoteForm({ ...currentQuoteForm, author: e.target.value })}
          />
          <div className="form-actions">
            {isEditingQuote ? (
              <>
                <button className="proposal-approve-btn" onClick={handleSaveQuoteEdit}>
                  Save Changes
                </button>
                <button className="proposal-cancel-btn" onClick={handleCancelEditQuote}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="proposal-approve-btn" onClick={handleAddQuote}>
                Add Quote
              </button>
            )}
          </div>
        </div>

        {/* Existing Quotes List */}
        {quotes.length > 0 && (
          <div className="resources-management-list" style={{ marginTop: 'var(--spacing-2xl)' }}>
            <h4>Existing Quotes ({quotes.length})</h4>
            {quotes.map((quote) => (
              <div key={quote.id} className="resource-management-item">
                <div className="resource-info">
                  <p style={{ margin: 'var(--spacing-xs) 0', color: 'var(--color-text-primary)', fontStyle: 'italic', fontSize: 'var(--font-size-lg)' }}>
                    "{quote.quote}"
                  </p>
                  {quote.author && (
                    <p style={{ margin: 'var(--spacing-xs) 0', color: 'var(--color-text-secondary)' }}>
                      — {quote.author}
                    </p>
                  )}
                  <span className="individual-dates" style={{ display: 'block', marginTop: 'var(--spacing-sm)' }}>
                    Added: {new Date(quote.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="proposal-actions">
                  <button className="proposal-edit-btn" onClick={() => handleStartEditQuote(quote)}>
                    Edit
                  </button>
                  <button className="proposal-reject-btn" onClick={() => handleDeleteQuote(quote.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {quotes.length === 0 && (
          <p className="no-posts" style={{ marginTop: 'var(--spacing-xl)' }}>
            No quotes added yet. Add your first quote above!
          </p>
        )}
      </div>
    </div>
  );
};

const wordOfDayComponents = {
  useWordOfDayManagement,
  WordOfDayDisplay,
  QuoteOfDayDisplay,
  WordOfDayManagementView
};

export default wordOfDayComponents;

